import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gem, Loader2, Boxes, ShieldCheck, PhoneCall, CheckCircle2 } from "lucide-react";
import CountryPhoneInput from "@/components/CountryPhoneInput";

const MSG91_WIDGET_ID = process.env.REACT_APP_MSG91_WIDGET_ID || "";
const MSG91_WIDGET_TOKEN = process.env.REACT_APP_MSG91_WIDGET_TOKEN || "";
const OTP_ENABLED = Boolean(MSG91_WIDGET_ID && MSG91_WIDGET_TOKEN);
const MSG91_SCRIPT_URLS = [
  "https://verify.msg91.com/otp-provider.js",
  "https://verify.phone91.com/otp-provider.js",
];

function loadMsg91Script() {
  return new Promise((resolve, reject) => {
    if (typeof window.initSendOTP === "function") return resolve();
    const existing = MSG91_SCRIPT_URLS.map((u) => document.querySelector(`script[src="${u}"]`)).find(Boolean);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("script load failed")));
      if (typeof window.initSendOTP === "function") resolve();
      return;
    }
    let i = 0;
    const attempt = () => {
      const s = document.createElement("script");
      s.src = MSG91_SCRIPT_URLS[i];
      s.async = true;
      s.onload = () => (typeof window.initSendOTP === "function" ? resolve() : reject(new Error("initSendOTP missing")));
      s.onerror = () => {
        i++;
        if (i < MSG91_SCRIPT_URLS.length) attempt();
        else reject(new Error("All MSG91 script URLs failed"));
      };
      document.head.appendChild(s);
    };
    attempt();
  });
}

/** Reusable OTP-guarded mobile input. Supports three channels — SMS
 *  (MSG91 default), WhatsApp (MSG91 with WA channel), and Email
 *  (Jeweltix-managed 6-digit code via Resend). When the primary MSG91
 *  send fails at the widget layer (403 for unrouteable countries etc.),
 *  we fire a server beacon with the country code AND automatically
 *  suggest the email fallback so international sellers never hit a wall.
 *  Returns the server-issued OTP proof (single-use, 10-min TTL) to the
 *  parent regardless of which channel produced it. */
export function MobileOtpField({ phone, setPhone, otpProof, setOtpProof, email = "", disabled = false, disabledHint = "" }) {
  const [channel, setChannel] = useState("sms"); // 'sms' | 'email'
  const [verifying, setVerifying] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [smsFailed, setSmsFailed] = useState(false); // set true after a widget failure so we show the friendly fallback panel

  useEffect(() => {
    if (!OTP_ENABLED) return;
    loadMsg91Script().catch((e) => console.warn("MSG91 script failed:", e));
  }, []);

  const digits = (phone || "").replace(/\D/g, "");
  const countryCode = digits.length > 10 ? `+${digits.slice(0, digits.length - 10)}` : "";
  const canVerify = digits.length >= 10 && !verifying && !otpProof && !disabled;

  // Fire-and-forget failure beacon so admins can see which countries are blocked.
  const reportFailure = (chan, err, httpStatus) => {
    try {
      fetch(`${window.__jx_apiRoot || ""}/api/otp/failure`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile: phone || "",
          channel: chan,
          error: (err || "").toString().slice(0, 400),
          http_status: httpStatus || null,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  };

  const runMsg91 = (chan) => {
    if (disabled) return toast.error(disabledHint || "Please complete the form above first.");
    if (digits.length < 10) return toast.error("Enter a valid mobile number first.");
    if (!OTP_ENABLED) { setSmsFailed(true); setChannel("email"); return; }
    setVerifying(true);
    loadMsg91Script().then(() => {
      // MSG91 widget config. `otpChannel: 11` selects WhatsApp when the
      // account has WhatsApp enabled; SMS is the default when omitted.
      const cfg = {
        widgetId: MSG91_WIDGET_ID,
        tokenAuth: MSG91_WIDGET_TOKEN,
        identifier: digits,
        exposeMethods: false,
        success: async (data) => {
          let rawToken = null;
          if (typeof data === "string") {
            rawToken = data.trim();
          } else if (data && typeof data === "object") {
            rawToken = (data.message || data.token || data.accessToken ||
                        data["access-token"] || data.access_token || data.data || "").toString().trim();
          }
          if (!rawToken || /^(verified|success|ok)$/i.test(rawToken)) {
            const dump = typeof data === "string" ? data : JSON.stringify(data || {});
            const m = dump.match(/e[yw][A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/);
            if (m) rawToken = m[0];
          }
          try {
            fetch(`${window.__jx_apiRoot || ""}/api/otp/msg91/debug`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                identifier: digits,
                raw_type: typeof data,
                raw_keys: (data && typeof data === "object") ? Object.keys(data).slice(0, 10) : [],
                raw_dump: (typeof data === "string" ? data : JSON.stringify(data || {})).slice(0, 400),
                extracted_len: rawToken ? rawToken.length : 0,
              }),
              keepalive: true,
            }).catch(() => {});
          } catch (_) { /* ignore */ }
          if (!rawToken) {
            setVerifying(false);
            return toast.error("OTP verified but no token was returned. Please try again.");
          }
          try {
            const { data: resp } = await api.post("/otp/msg91/verify", { access_token: rawToken, identifier: digits });
            setOtpProof(resp?.proof_id || rawToken);
            toast.success("Mobile verified ✓");
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Server rejected the OTP. Try again.");
          } finally {
            setVerifying(false);
          }
        },
        failure: (err) => {
          setVerifying(false);
          setSmsFailed(true);
          const errMsg = err?.message || err?.error || (typeof err === "string" ? err : JSON.stringify(err || {}));
          // Extract MSG91's HTTP status from the widget's error shape if we can
          const statusMatch = /(\d{3})/.exec(errMsg || "");
          reportFailure(chan, errMsg, statusMatch ? parseInt(statusMatch[1], 10) : null);
          // Don't show the raw upstream error to the user — swap to a
          // clear message + suggest the email fallback.
          toast.error("We couldn't send an OTP to this number. Please try a different verification method.");
        },
      };
      // 2026-03-01 — SMS is the ONLY MSG91 channel we expose today.
      // WhatsApp was tried and pulled: MSG91's widget-channel WhatsApp
      // OTP proved unreliable across the international markets we care
      // about, so Email became the sole fallback. If SMS won't route,
      // the failure handler drops the user into the Email path where
      // their mobile is still captured and marked pending-manual-review.
      window.initSendOTP(cfg);
    }).catch((e) => {
      setVerifying(false);
      setSmsFailed(true);
      reportFailure(chan, `script load failed: ${e?.message || e}`, null);
      toast.error("We couldn't send an OTP to this number. Please try a different verification method.");
    });
  };

  const sendEmailCode = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter your email above first — we'll send the code there.");
      return;
    }
    // 2026-03-01 — REQUIRE the mobile number even for email verification
    // so international sellers who fall back to email still leave their
    // mobile on file. Admin marks it verified after a manual check.
    if (digits.length < 10) {
      toast.error("Please enter your mobile number first — we still record it with your account.");
      return;
    }
    setVerifying(true);
    try {
      await api.post("/otp/email/send", { email: email.trim().toLowerCase(), mobile: phone });
      setEmailCodeSent(true);
      toast.success(`Code sent to ${email}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send code — please try again.";
      toast.error(msg);
      reportFailure("email", msg, err?.response?.status || null);
    } finally {
      setVerifying(false);
    }
  };

  const verifyEmailCode = async () => {
    if (!/^\d{6}$/.test(emailCode)) return toast.error("Enter the 6-digit code from your email.");
    setVerifying(true);
    try {
      // We ALWAYS send the mobile here too — the backend pairs it onto
      // the resulting otp_proofs doc so /auth/register captures the
      // number without a second flow.
      const { data: resp } = await api.post("/otp/email/verify", {
        email: email.trim().toLowerCase(),
        code: emailCode,
        mobile: phone,
      });
      setOtpProof(resp?.proof_id);
      toast.success("Verified ✓ — your mobile is recorded (pending team review) and you can continue.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setVerifying(false);
    }
  };

  const ChannelButton = ({ id, label, sub }) => (
    <button
      type="button"
      onClick={() => { setChannel(id); setEmailCodeSent(false); setEmailCode(""); }}
      data-testid={`otp-channel-${id}`}
      className={`flex-1 border rounded-md px-2.5 py-2 text-left text-[11px] transition-colors ${channel === id ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
    >
      <div className="font-semibold text-xs">{label}</div>
      <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{sub}</div>
    </button>
  );

  return (
    <div className={`mt-4 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
      <label className="block">
        <span className="text-sm font-medium">Mobile number</span>
        <div className="mt-1">
          <CountryPhoneInput
            value={phone}
            onChange={(v) => {
              const oldTail = (phone || "").replace(/\D/g, "").slice(-10);
              const newTail = (v || "").replace(/\D/g, "").slice(-10);
              setPhone(v);
              if (otpProof && oldTail && newTail && oldTail !== newTail) {
                setOtpProof(null);
                setEmailCodeSent(false);
                setEmailCode("");
                setSmsFailed(false);
              }
            }}
            testid="reg-phone"
            verified={Boolean(otpProof)}
            disabled={Boolean(otpProof) || disabled}
          />
        </div>
      </label>

      {!otpProof && !disabled && (
        <>
          {/* Channel picker — SMS (primary) + Email (fallback). WhatsApp
              was removed 2026-03-01 after unreliable widget-channel
              routing; Email now carries every non-SMS market. */}
          <div className="flex gap-2 mt-3" data-testid="otp-channel-row">
            <ChannelButton id="sms"   label="SMS"   sub="Text message" />
            <ChannelButton id="email" label="Email" sub="If SMS won't reach" />
          </div>

          {/* SMS failure banner — surfaces WHY we're recommending email. */}
          {smsFailed && channel !== "email" && (
            <div className="mt-3 border border-amber-400 bg-amber-50 rounded-md p-3 text-xs text-amber-900" data-testid="otp-fallback-banner">
              We couldn't send an OTP to <span className="font-mono">{countryCode || "this number"}</span>. Switch to <button type="button" className="underline font-semibold" onClick={() => setChannel("email")}>Email verification</button> — we'll still keep your mobile on file.
            </div>
          )}

          {/* Action row per channel */}
          {channel === "sms" && OTP_ENABLED && (
            <button
              type="button"
              onClick={() => runMsg91("sms")}
              disabled={!canVerify}
              data-testid="verify-mobile-btn"
              className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
              {verifying ? "Sending SMS…" : "Send SMS OTP"}
            </button>
          )}
          {channel === "email" && (
            <div className="mt-3 space-y-2">
              <p className="text-[11px] text-muted-foreground">
                We'll email a 6-digit code to <span className="font-mono">{email || "your email above"}</span>. Your mobile <span className="font-mono">{countryCode ? `${countryCode} …${digits.slice(-4)}` : ""}</span> stays on file — our team confirms it once you're in.
              </p>
              {!emailCodeSent ? (
                <button
                  type="button"
                  onClick={sendEmailCode}
                  disabled={verifying || !email || digits.length < 10}
                  data-testid="verify-mobile-email-send"
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {verifying ? "Sending code…" : (digits.length < 10 ? "Enter mobile above first" : "Email me a code")}
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    inputMode="numeric"
                    maxLength={6}
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="email-otp-code-input"
                    placeholder="6-digit code"
                    className="flex-1 bg-card border border-border rounded-md px-3 py-2.5 text-sm text-center font-mono tracking-widest focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={verifyEmailCode}
                    disabled={verifying || emailCode.length !== 6}
                    data-testid="email-otp-verify"
                    className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
                  >
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                  </button>
                </div>
              )}
              {emailCodeSent && (
                <button
                  type="button"
                  onClick={() => { setEmailCodeSent(false); setEmailCode(""); }}
                  className="text-[11px] text-muted-foreground underline"
                  data-testid="email-otp-resend"
                >
                  Didn't get it? Send again
                </button>
              )}
            </div>
          )}
        </>
      )}

      {otpProof && (
        <div
          data-testid="otp-verified-badge"
          className="mt-3 flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500 text-emerald-600 dark:text-emerald-400 text-sm font-semibold"
        >
          <CheckCircle2 className="h-4 w-4" /> Verified — you can create your account
        </div>
      )}

      {disabled && disabledHint && (
        <p className="mt-1.5 text-[11px] text-amber-500 font-medium">{disabledHint}</p>
      )}
    </div>
  );
}

function GoogleButton({ onClick, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="google-login"
      className={
        primary
          ? "w-full flex items-center justify-center gap-2 border border-border rounded-md py-3 font-semibold hover:bg-secondary transition-colors shadow-sm"
          : "w-full flex items-center justify-center gap-2 border border-border rounded-md py-2.5 font-medium hover:bg-secondary transition-colors"
      }
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
      Continue with Google
    </button>
  );
}

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || null;
  const [showEmailForm, setShowEmailForm] = useState(isLogin); // login page defaults to showing form; signup starts collapsed
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer", phone: "" });
  const [loading, setLoading] = useState(false);
  const [otpProof, setOtpProof] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && OTP_ENABLED && !otpProof) {
      toast.error("Please verify your mobile number with the OTP before creating your account.");
      return;
    }
    setLoading(true);
    try {
      let u;
      if (isLogin) {
        u = await login(form.email, form.password);
      } else {
        u = await register({
          ...form,
          otp_proof_id: otpProof || undefined,
        });
      }
      toast.success(isLogin ? "Welcome back" : "Account created");
      const dest = from || (u.role === "admin" || u.role === "employee" ? "/admin" : u.role === "seller" ? "/seller" : "/buyer");
      nav(dest);
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 mesh-grid opacity-40" />
        <Link to="/" className="relative flex items-center gap-2">
          <Gem className="h-7 w-7 text-primary" strokeWidth={1.5} />
          <span className="font-display font-extrabold text-xl">Jewel<span className="text-primary">tix</span></span>
        </Link>
        <div className="relative">
          <h2 className="font-display font-extrabold text-4xl leading-tight">Production-ready jewellery CAD, at your fingertips.</h2>
          <div className="mt-8 space-y-3">
            {[["Live 3D technical previews", Boxes], ["Escrow-protected payments", ShieldCheck]].map(([t, Icon]) => (
              <div key={t} className="flex items-center gap-3 text-slate-300"><Icon className="h-5 w-5 text-primary" /> {t}</div>
            ))}
          </div>
        </div>
        <p className="relative font-mono text-xs text-slate-500">© 2026 Jeweltix — 3DM & STL marketplace</p>
      </div>

      <div className="flex items-center justify-center p-6 py-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <Gem className="h-7 w-7 text-primary" />
            <span className="font-display font-extrabold text-xl">Jeweltix</span>
          </Link>
          <h1 className="font-display font-bold text-2xl">{isLogin ? "Log in" : "Create your account"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isLogin ? "Access your dashboard and downloads." : "Get started in seconds — Google or email, your choice."}
          </p>

          {/* PRIMARY CTA — Google first, always. */}
          <div className="mt-6">
            <GoogleButton onClick={loginWithGoogle} primary />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">
              or {isLogin ? "log in with email" : "sign up with email"}
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* SECONDARY — Email form. Collapsed on signup by default; user can expand. */}
          {!isLogin && !showEmailForm ? (
            <button
              type="button"
              onClick={() => setShowEmailForm(true)}
              data-testid="show-email-signup"
              className="w-full border border-dashed border-border rounded-md py-2.5 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              Use email instead
            </button>
          ) : (
            <form onSubmit={submit}>
              {!isLogin && (
                <div className="grid grid-cols-3 gap-2">
                  {[["buyer", "Buyer", "Manufacturer"], ["seller", "Designer", "Sell files"], ["both", "Both", "Buy & sell"]].map(([r, t, s]) => (
                    <button
                      type="button"
                      key={r}
                      data-testid={`role-${r}`}
                      onClick={() => setForm({ ...form, role: r })}
                      className={`border rounded-md p-3 text-left transition-colors ${form.role === r ? "border-primary bg-primary/5" : "border-border"}`}
                    >
                      <p className="font-semibold text-sm">{t}</p>
                      <p className="text-[11px] text-muted-foreground">{s}</p>
                    </button>
                  ))}
                </div>
              )}
              {!isLogin && (
                <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="reg-name" />
              )}
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="auth-email" />
              <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="auth-password" />
              {isLogin && (
                <div className="mt-1 text-right">
                  <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs text-primary font-medium hover:underline">
                    Forgot password?
                  </Link>
                </div>
              )}
              {!isLogin && (() => {
                // Mobile + OTP is the LAST step. Gated behind name + email +
                // password (basic format check) so we don't spend an SMS on
                // tire-kickers who fill in a phone number and bail.
                const nameOk = (form.name || "").trim().length >= 2;
                const emailOk = /^\S+@\S+\.\S+$/.test(form.email || "");
                const passwordOk = (form.password || "").length >= 6;
                const otpGated = !(nameOk && emailOk && passwordOk);
                const hint = otpGated
                  ? `Please fill your ${[!nameOk && "name", !emailOk && "email", !passwordOk && "password (min 6 chars)"].filter(Boolean).join(", ")} above first.`
                  : "";
                return (
                  <MobileOtpField
                    phone={form.phone}
                    setPhone={(v) => setForm({ ...form, phone: v })}
                    email={form.email}
                    otpProof={otpProof}
                    setOtpProof={setOtpProof}
                    disabled={otpGated}
                    disabledHint={hint}
                  />
                );
              })()}

              <button
                disabled={loading || (!isLogin && OTP_ENABLED && !otpProof)}
                data-testid="auth-submit"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-50"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} {isLogin ? "Log in" : "Create account"}
              </button>
            </form>
          )}

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {isLogin ? "New to Jeweltix? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-primary font-medium">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
          <p className="text-[11px] text-muted-foreground mt-4 text-center">
            By continuing, you agree to review and accept our Terms &amp; Privacy Policy after signing in.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <label className="block mt-4">
      <span className="text-sm font-medium">{label}</span>
      <input
        type={type}
        required
        value={value}
        data-testid={testid}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
      />
    </label>
  );
}
